import { Injectable } from '@angular/core';
import { ExerciseSet } from '../models/exercise-set.interface';
import { ExerciseValidationService } from './exercise-validation.service';

interface Exercise {
  id: number;
  exercise: string;
  exerciseId?: string;
  allotmentId?: string;
  sets: ExerciseSet[];
  section?: 'warmup' | 'main' | 'cooldown';
  isMarkedForDeletion?: boolean;
}

interface DateItem {
  date: Date;
  formattedDate: string;
  dayName: string;
  isExpanded: boolean;
  exercises: Exercise[];
  isRestDay: boolean;
  restDayAllotmentId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExerciseStatisticsService {
  constructor(private exerciseValidationService: ExerciseValidationService) {}

  /**
   * Get total sets count for a specific date
   */
  getTotalSetsCount(dateItem: DateItem): number {
    if (dateItem.isRestDay) {
      return 0;
    }

    // Only count active exercises (not marked for deletion)
    const activeExercises = dateItem.exercises.filter(
      (ex) => !ex.isMarkedForDeletion
    );

    return activeExercises.reduce((total, exercise) => {
      if (exercise.exercise && exercise.exercise.trim() !== '') {
        return total + exercise.sets.length;
      }
      return total;
    }, 0);
  }

  /**
   * Get completed sets count for a specific date
   */
  getCompletedSetsCount(dateItem: DateItem): number {
    if (dateItem.isRestDay) {
      return 0;
    }

    // Only count active exercises (not marked for deletion)
    const activeExercises = dateItem.exercises.filter(
      (ex) => !ex.isMarkedForDeletion
    );

    return activeExercises.reduce((total, exercise) => {
      if (exercise.exercise && exercise.exercise.trim() !== '') {
        const completedSets = exercise.sets.filter((set) =>
          this.isSetCompleted(set)
        );
        return total + completedSets.length;
      }
      return total;
    }, 0);
  }

  /**
   * Check if date has completed exercises
   */
  hasCompletedExercises(dateItem: DateItem): boolean {
    return this.getCompletedSetsCount(dateItem) > 0;
  }

  /**
   * Get pending deletion count
   */
  getPendingDeletionCount(pendingDeletions: Set<string>): number {
    return pendingDeletions.size;
  }

  /**
   * Check if exercise is marked for deletion
   */
  isExerciseMarkedForDeletion(exercise: Exercise): boolean {
    return !!exercise.isMarkedForDeletion;
  }

  /**
   * Get list of exercises pending deletion
   */
  getPendingDeletionExercises(
    dateList: DateItem[]
  ): { date: string; exercise: string }[] {
    const pendingExercises: { date: string; exercise: string }[] = [];

    dateList.forEach((dateItem) => {
      const markedExercises = dateItem.exercises.filter(
        (ex) => ex.isMarkedForDeletion
      );
      markedExercises.forEach((exercise) => {
        if (exercise.exercise && exercise.exercise.trim() !== '') {
          pendingExercises.push({
            date: dateItem.formattedDate,
            exercise: exercise.exercise,
          });
        }
      });
    });

    return pendingExercises;
  }

  /**
   * Get exercise count by section for a specific date
   */
  getExerciseCountBySection(
    dateItem: DateItem,
    section: 'warmup' | 'main' | 'cooldown'
  ): number {
    if (dateItem.isRestDay) {
      return 0;
    }

    // Only count active exercises (not marked for deletion)
    const activeExercises = dateItem.exercises.filter(
      (ex) =>
        !ex.isMarkedForDeletion &&
        ex.section === section &&
        ex.exercise &&
        ex.exercise.trim() !== ''
    );

    return activeExercises.length;
  }

  /**
   * Get total exercise count for a specific date
   */
  getTotalExerciseCount(dateItem: DateItem): number {
    if (dateItem.isRestDay) {
      return 0;
    }

    // Only count active exercises (not marked for deletion)
    const activeExercises = dateItem.exercises.filter(
      (ex) =>
        !ex.isMarkedForDeletion && ex.exercise && ex.exercise.trim() !== ''
    );

    return activeExercises.length;
  }

  /**
   * Get completion percentage for a specific date
   */
  getCompletionPercentage(dateItem: DateItem): number {
    const totalSets = this.getTotalSetsCount(dateItem);
    if (totalSets === 0) return 0;

    const completedSets = this.getCompletedSetsCount(dateItem);
    return Math.round((completedSets / totalSets) * 100);
  }

  /**
   * Get workout summary for a specific date
   */
  getWorkoutSummary(dateItem: DateItem): {
    totalExercises: number;
    totalSets: number;
    completedSets: number;
    completionPercentage: number;
    bySection: {
      warmup: number;
      main: number;
      cooldown: number;
    };
  } {
    return {
      totalExercises: this.getTotalExerciseCount(dateItem),
      totalSets: this.getTotalSetsCount(dateItem),
      completedSets: this.getCompletedSetsCount(dateItem),
      completionPercentage: this.getCompletionPercentage(dateItem),
      bySection: {
        warmup: this.getExerciseCountBySection(dateItem, 'warmup'),
        main: this.getExerciseCountBySection(dateItem, 'main'),
        cooldown: this.getExerciseCountBySection(dateItem, 'cooldown'),
      },
    };
  }

  /**
   * Check if set is completed (delegates to validation service)
   */
  private isSetCompleted(set: ExerciseSet): boolean {
    return this.exerciseValidationService.isSetCompleted(set);
  }

  /**
   * Get overall workout plan statistics
   */
  getOverallStatistics(dateList: DateItem[]): {
    totalDays: number;
    workoutDays: number;
    restDays: number;
    completedDays: number;
    totalExercises: number;
    totalSets: number;
    completedSets: number;
    overallCompletion: number;
  } {
    const workoutDays = dateList.filter((d) => !d.isRestDay);
    const restDays = dateList.filter((d) => d.isRestDay);
    const completedDays = workoutDays.filter((d) =>
      this.hasCompletedExercises(d)
    );

    const totalExercises = dateList.reduce(
      (sum, d) => sum + this.getTotalExerciseCount(d),
      0
    );
    const totalSets = dateList.reduce(
      (sum, d) => sum + this.getTotalSetsCount(d),
      0
    );
    const completedSets = dateList.reduce(
      (sum, d) => sum + this.getCompletedSetsCount(d),
      0
    );

    const overallCompletion =
      totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

    return {
      totalDays: dateList.length,
      workoutDays: workoutDays.length,
      restDays: restDays.length,
      completedDays: completedDays.length,
      totalExercises,
      totalSets,
      completedSets,
      overallCompletion,
    };
  }

  /**
   * Get count of incomplete exercises
   * @param exercises - Array of exercises to check
   * @param validationService - Exercise validation service instance
   * @returns Number of incomplete exercises
   */
  getIncompleteExercisesCount(
    exercises: Exercise[],
    validationService: any
  ): number {
    if (!exercises || exercises.length === 0) return 0;

    const activeExercises = exercises.filter((ex) => !ex.isMarkedForDeletion);
    return activeExercises.filter(
      (exercise) => !validationService.isExerciseComplete(exercise)
    ).length;
  }
}
